import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, fileUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { GoldBadge, PILLARS } from "@/constants/pillars";
import CardManager from "@/components/CardManager";
import ConnectCard from "@/components/ConnectCard";
import CommissionsCard from "@/components/CommissionsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, LogOut, TrendingUp, BarChart3 } from "lucide-react";
import PushCard from "@/components/PushCard";
import CashbackCard from "@/components/CashbackCard";
import { useNavigate } from "react-router-dom";

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t, setLang: setI18nLang } = useI18n();
  const [earnings, setEarnings] = useState(null);
  const [bio, setBio] = useState(user?.bio || "");
  const [name, setName] = useState(user?.name || "");
  const [title, setTitle] = useState(user?.title || "");
  const [pillar, setPillar] = useState(user?.pillar || "asset");
  const [phone, setPhone] = useState(user?.phone || "");
  const [location, setLocation] = useState(user?.location || "");
  const [appOpen, setAppOpen] = useState(false);
  const [appForm, setAppForm] = useState({ type: "specialist", name: user?.name || "", email: user?.email || "", phone: "", pillar: "asset", message: "" });
  const [stats, setStats] = useState(null);
  const fileRef = useRef();

  const loadStats = async () => {
    if (user?.role !== "admin") return;
    try { const { data } = await api.get("/admin/data-stats"); setStats(data); } catch {}
  };
  const load = async () => {
    const { data } = await api.get("/me/earnings");
    setEarnings(data);
    loadStats();
  };
  useEffect(() => { load(); }, []);

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/me/avatar", fd); await refreshUser(); toast.success("Avatar aggiornato"); } catch { toast.error("Errore"); }
  };
  const saveProfile = async () => { await api.patch("/me/profile", { name, bio, title, pillar, phone, location }); await refreshUser(); toast.success("Profilo salvato"); };
  const setLang = async (lang) => { setI18nLang(lang); await api.patch("/me/lang", { lang }); await refreshUser(); toast.success("OK"); };
  const resetData = async () => {
    if (!window.confirm("Sei sicuro? Verranno cancellati TUTTI i dati (post, offerte, prenotazioni, utenti registrati, notifiche...) tranne il tuo account admin. Operazione irreversibile.")) return;
    try { await api.post("/admin/reset-data"); toast.success("Tutti i dati sono stati azzerati"); await refreshUser(); loadStats(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteAccount = async () => {
    if (!window.confirm("Eliminare definitivamente il tuo account, i tuoi post, offerte e commenti? Lo storico pagamenti resta per contabilità. Operazione irreversibile.")) return;
    try { await api.delete("/me"); toast.success("Account eliminato"); logout(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitApp = async () => {
    try { await api.post("/applications", appForm); setAppOpen(false); toast.success("Candidatura inviata"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="relative">
          <Avatar className="h-20 w-20"><AvatarImage src={fileUrl(user?.avatar_url)} /><AvatarFallback className="bg-black text-white text-2xl">{user?.name?.[0]}</AvatarFallback></Avatar>
          <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()} className="absolute bottom-0 right-0 bg-black text-white rounded-full p-1.5"><Camera size={14} /></button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadAvatar} />
        </div>
        <div>
          <div className="flex items-center gap-2"><h1 className="font-display text-xl font-bold">{user?.name}</h1>{user?.is_gold && <GoldBadge />}</div>
          <p className="text-sm text-neutral-500 capitalize">{user?.role}{user?.title ? ` · ${user.title}` : ""}</p>
          <p className="text-xs text-neutral-400">{user?.email}</p>
        </div>
      </div>

      {/* Earnings */}
      <div>
        <h2 className="font-display font-bold mb-2 flex items-center gap-2"><TrendingUp size={18} />{t("earnings")}</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-black text-white rounded-2xl p-4"><p className="text-xs text-neutral-400">{t("total_earned")}</p><p className="font-display text-2xl font-extrabold">€{earnings?.total_earned?.toFixed(2)}</p></div>
          <div className="border border-neutral-200 rounded-2xl p-4"><p className="text-xs text-neutral-500">{t("net_sales")}</p><p className="font-display text-2xl font-extrabold">€{earnings?.net_sales?.toFixed(2)}</p></div>
          <div className="border border-neutral-200 rounded-2xl p-4"><p className="text-xs text-neutral-500">{t("commissions_paid")}</p><p className="font-display text-lg font-bold">€{earnings?.commissions_paid?.toFixed(2)}</p></div>
          <div className="border border-neutral-200 rounded-2xl p-4"><p className="text-xs text-neutral-500">{t("pending")}</p><p className="font-display text-lg font-bold text-amber-600">€{earnings?.commissions_pending?.toFixed(2)}</p></div>
        </div>
        <button data-testid="cashflow-link" onClick={() => navigate("/flusso")}
          className="mt-3 w-full flex items-center justify-between rounded-2xl border border-neutral-200 px-4 py-3 hover:border-black transition-colors">
          <span className="flex items-center gap-2 font-semibold text-sm"><BarChart3 size={18} />Flusso di denaro e statistiche</span>
          <span className="text-xs text-neutral-500">Grafici entrate/uscite →</span>
        </button>
      </div>

      {/* Push notifications */}
      <PushCard />

      {/* Cashback */}
      <CashbackCard />

      {/* Affiliazione */}
      <div>
        <h2 className="font-display font-bold mb-2">{t("referral")}</h2>
        <button data-testid="referral-dashboard-link" onClick={() => navigate("/affiliazione")} className="w-full flex items-center justify-between rounded-2xl border border-neutral-200 px-4 py-3 hover:border-black transition-colors">
          <span className="font-semibold text-sm">Programma Affiliazione</span><span className="text-xs text-neutral-500">Link, invitati, commissioni →</span>
        </button>
      </div>

      {/* Passo 1: Stripe Connect (obbligatorio) / admin: commissioni */}
      {user?.role === "admin" ? <CommissionsCard /> : <ConnectCard />}

      {/* Passo 2: metodi di pagamento */}
      <CardManager t={t} />

      {/* Profile edit */}
      <div>
        <h2 className="font-display font-bold mb-2">{t("personal_profile")}</h2>
        <p className="text-xs text-neutral-500 mb-2">{t("personal_profile_hint")}</p>
        <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("name")} className="h-11 mb-2" />
        <Input data-testid="profile-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo professionale (es. Web Designer)" className="h-11 mb-2" />
        <div className="flex gap-2 mb-2">
          <Input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono" className="h-11" />
          <Input data-testid="profile-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Località" className="h-11" />
        </div>
        <Select value={pillar} onValueChange={setPillar}>
          <SelectTrigger data-testid="profile-pillar" className="h-11 mb-2"><SelectValue placeholder="Pilastro" /></SelectTrigger>
          <SelectContent>{PILLARS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.desc}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Bio / descrizione dei tuoi servizi" />
        <Button data-testid="save-profile-btn" onClick={saveProfile} className="w-full mt-2 h-11 rounded-full bg-black text-white">{t("save_profile")}</Button>
      </div>

      {/* Language */}
      <div>
        <h2 className="font-display font-bold mb-2">{t("language")}</h2>
        <Select value={user?.lang || "it"} onValueChange={setLang}>
          <SelectTrigger data-testid="lang-select" className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="it">🇮🇹 Italiano</SelectItem>
            <SelectItem value="ro">🇷🇴 Română</SelectItem>
            <SelectItem value="en">🇬🇧 English</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button data-testid="apply-btn" onClick={() => setAppOpen(true)} variant="outline" className="w-full h-11 rounded-full">Candidati come specialista/promotore</Button>

      {user?.role === "admin" && (
        <div className="border border-red-200 rounded-2xl p-4">
          <h2 className="font-display font-bold mb-1 text-red-600">Zona pericolosa (admin)</h2>
          <p className="text-xs text-neutral-500 mb-3">Cancella tutti i dati di test/esempio e le registrazioni. Rimane solo il tuo account admin. Utile per ripartire da zero dopo la pubblicazione.</p>
          <p data-testid="data-stats-total" className="text-sm mb-3">
            Dati presenti nel database: <b>{stats ? stats.total : "…"}</b>
            {stats && stats.total === 0 && <span className="text-green-600 ml-1">— database vuoto, pronto al lancio</span>}
          </p>
          <Button data-testid="reset-data-profile-btn" onClick={resetData} className="w-full h-11 rounded-full bg-red-600 hover:bg-red-700 text-white">Rimuovi tutti i dati</Button>
        </div>
      )}

      <Button data-testid="profile-logout-btn" onClick={logout} variant="outline" className="w-full h-11 rounded-full border-red-200 text-red-600"><LogOut size={16} className="mr-1" />Esci</Button>

      {user?.role !== "admin" && (
        <button data-testid="delete-account-btn" onClick={deleteAccount} className="w-full text-xs text-neutral-400 hover:text-red-600 underline underline-offset-2 py-2">
          Elimina il mio account e i miei contenuti
        </button>
      )}


      {/* Application dialog */}
      <Dialog open={appOpen} onOpenChange={setAppOpen}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">Candidatura</DialogTitle></DialogHeader>
          <Select value={appForm.type} onValueChange={(v) => setAppForm({ ...appForm, type: v })}>
            <SelectTrigger data-testid="app-type" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="specialist">Specialista</SelectItem><SelectItem value="promoter">Promotore</SelectItem></SelectContent>
          </Select>
          <Input data-testid="app-name" placeholder="Nome" value={appForm.name} onChange={(e) => setAppForm({ ...appForm, name: e.target.value })} className="h-11" />
          <Input data-testid="app-email" placeholder="Email" value={appForm.email} onChange={(e) => setAppForm({ ...appForm, email: e.target.value })} className="h-11" />
          <Input data-testid="app-phone" placeholder="Telefono" value={appForm.phone} onChange={(e) => setAppForm({ ...appForm, phone: e.target.value })} className="h-11" />
          <Select value={appForm.pillar} onValueChange={(v) => setAppForm({ ...appForm, pillar: v })}>
            <SelectTrigger data-testid="app-pillar" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>{PILLARS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea data-testid="app-message" placeholder="Messaggio" value={appForm.message} onChange={(e) => setAppForm({ ...appForm, message: e.target.value })} />
          <Button data-testid="app-submit" onClick={submitApp} className="w-full h-11 rounded-full bg-black text-white">Invia candidatura</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
